import downloadFile from "./downloadFile";
import handleErrors from "./handleErrors";

/**
 * Options for creating a reusable onQueryStarted handler that downloads files.
 */
export type FetchFileOnQueryStartedOptions<TData, TArg> = {
  /**
   * The name to save the downloaded file as.
   */
  fileName: string;
  /**
   * Optional resolver to extract the URL from the query result data.
   * If omitted, the result data is assumed to be a string URL.
   */
  resolveUrl?: (data: TData, arg: TArg) => string;
  /**
   * Optional callback invoked after the file is downloaded.
   */
  onDownloaded?: (url: string, data: TData, arg: TArg) => void | Promise<void>;
};

/**
 * Factory for an RTK Query `onQueryStarted` handler tailored for export/file download endpoints.
 * It awaits the query, resolves the file URL, triggers a download, and handles errors uniformly.
 */
const fetchFileOnQueryStarted =
  <TData = string, TArg = unknown>(
    options: FetchFileOnQueryStartedOptions<TData, TArg>
  ) =>
  async (
    arg: TArg,
    context: { queryFulfilled: Promise<{ data: TData }> }
  ): Promise<void> => {
    const { queryFulfilled } = context;

    try {
      const { data } = await queryFulfilled;
      const url = options.resolveUrl
        ? options.resolveUrl(data, arg)
        : (data as unknown as string);

      if (typeof url !== "string" || !url) {
        throw new Error("Invalid file URL returned from export endpoint");
      }

      downloadFile(url, options.fileName);
      await options.onDownloaded?.(url, data, arg);
    } catch (error) {
      handleErrors(error);
    }
  };

export default fetchFileOnQueryStarted;
